import { waitFor } from "utils";
import type {
  IntentIqConfig,
  IntentIqObject,
  IntentIqReportData,
} from "./types";

type IntentIqObjectVersion = "default" | "v6_1";

type IntentIqObjectName = "IntentIqObject" | "IntentIqObject61";

const PREBID_BIDDING_PLATFORM_ID = 1;

const FANDOM_ID: Record<IntentIqObjectVersion, number> = {
  default: 1187275693,
  v6_1: 339079598,
};

const INTENT_IQ_OBJECT_NAME: Record<IntentIqObjectVersion, IntentIqObjectName> =
  {
    default: "IntentIqObject",
    v6_1: "IntentIqObject61",
  };

declare global {
  interface Window {
    IntentIqObject?: IntentIqObject;
    IntentIqObject61?: IntentIqObject;
  }
}

export function bidToReport(bid: PrebidBidResponse): IntentIqReportData {
  return {
    biddingPlatformId: PREBID_BIDDING_PLATFORM_ID,
    partnerAuctionId: bid.auctionId,
    bidderCode: bid.bidderCode,
    prebidAuctionId: bid.auctionId,
    cpm: bid.cpm,
    currency: bid.currency,
  };
}

export function getIntentIqScriptUrl(version: string): string {
  return `//script.wikia.nocookie.net/fandom-ae-assets/intentiq/${version}/IIQUniversalID.js`;
}

export function isIntentIq61Version(version: string): boolean {
  return version === "v6.1";
}

export function getIntentIqObjectName(version: string): IntentIqObjectName {
  return INTENT_IQ_OBJECT_NAME[
    isIntentIq61Version(version) ? "v6_1" : "default"
  ];
}

export function getIntentIqObject(version: string): IntentIqObject | undefined {
  return window[getIntentIqObjectName(version)];
}

export async function waitUntilIntentIqObjectIsAvailable(
  version: string,
): Promise<void> {
  await waitFor(() => getIntentIqObject(version) !== undefined, {
    maxTries: 10,
    exponentialTimeout: 10,
  });
}

export function getIntentIqConfig(
  version: string,
  timeout: number,
  pbjs: Prebid,
): IntentIqConfig {
  const domainName = window.location.hostname.includes(".fandom.com")
    ? "fandom.com"
    : window.location.hostname;

  if (isIntentIq61Version(version)) {
    return {
      partner: FANDOM_ID.v6_1,
      pbjs,
      timeoutInMillis: timeout,
      ABTestingConfigurationSource: "IIQServer",
      manualWinReportEnabled: true,
      domainName,
      vrBrowserBlackList: ["chrome"],
    };
  }

  return {
    partner: FANDOM_ID.default,
    pbjs,
    timeoutInMillis: timeout,
    ABTestingConfigurationSource: "percentage",
    abPercentage: 97,
    manualWinReportEnabled: true,
    domainName,
    vrBrowserBlackList: ["chrome"],
  };
}

export async function getIntentIqObjectInstance61(
  intentIqConfig: IntentIqConfig,
  intentIqObjectOnWindow: IntentIqObject,
  readyCallback: (
    intentIqConfig: IntentIqObject,
    abTestingGroup: string,
  ) => void,
): Promise<void> {
  // Get the 6.1 version of the object, ab testing group is passed in the callback
  return new Promise<void>((resolve) => {
    const intentIqObjectInstance = new intentIqObjectOnWindow({
      ...intentIqConfig,
      callback: (data, abTestGroup) => {
        abTestGroup ??= "U";
        readyCallback(intentIqObjectInstance, abTestGroup);
        resolve();
      },
    });
  });
}

export async function getIntentIqObjectInstance60(
  intentIqConfig: IntentIqConfig,
  intentIqObjectOnWindow: IntentIqObject,
  readyCallback: (
    intentIqConfig: IntentIqObject,
    abTestingGroup: string,
  ) => void,
): Promise<void> {
  // Get the default version of the object, ab testing group is available in the object
  return new Promise<void>((resolve) => {
    const intentIqObjectInstance = new intentIqObjectOnWindow({
      ...intentIqConfig,
      callback: () => {
        resolve();
      },
    });
    const abTestGroup =
      intentIqObjectInstance?.intentIqConfig?.abTesting?.currentTestGroup ??
      "U";
    readyCallback(intentIqObjectInstance, abTestGroup);
  });
}

export async function getIntentIqObjectInstance(
  version: string,
  timeout: number,
  pbjs: Prebid,
  readyCallback: (
    intentIqConfig: IntentIqObject,
    abTestingGroup: string,
  ) => void,
): Promise<void> {
  const intentIqConfig = getIntentIqConfig(version, timeout, pbjs);
  const intentIqObjectOnWindow = getIntentIqObject(version);

  if (!intentIqObjectOnWindow) {
    return;
  }

  return isIntentIq61Version(version)
    ? getIntentIqObjectInstance61(
        intentIqConfig,
        intentIqObjectOnWindow,
        readyCallback,
      )
    : getIntentIqObjectInstance60(
        intentIqConfig,
        intentIqObjectOnWindow,
        readyCallback,
      );
}
